import React from "react";
import { Controller, Control, FieldPath, FieldValues } from "react-hook-form";
import { Field, Input, Label } from "@fluentui/react-components";
import InputField from "./InputField";

interface HookFormTextInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  type?: "text" | "email" | "password" | "tel" | "url" | "number";
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  helperText?: string;
  size?: "small" | "medium" | "large";
  appearance?: "outline" | "underline" | "filled-darker" | "filled-lighter";
  autoComplete?: string;
  readOnly?: boolean;
  contentAfter?: any;
}

const HookFormTextInputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  placeholder,
  required = false,
  disabled = false,
  type = "text",
  maxLength,
  minLength,
  pattern,
  helperText,
  size = "medium",
  appearance = "outline",
  autoComplete = "off",
  readOnly = false,
  contentAfter = null,
}: HookFormTextInputFieldProps<TFieldValues, TName>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? "This field is required" : false,
        maxLength: maxLength
          ? {
              value: maxLength,
              message: `Maximum ${maxLength} characters allowed`,
            }
          : undefined,
        minLength: minLength
          ? {
              value: minLength,
              message: `Minimum ${minLength} characters required`,
            }
          : undefined,
        pattern: pattern
          ? {
              value: new RegExp(pattern),
              message: "Invalid format",
            }
          : undefined,
      }}
      render={({ field, fieldState }) => (
        <Field
          validationMessage={fieldState.error?.message}
          validationState={fieldState.error ? "error" : "none"}
          hint={helperText}
        >
          <InputField
            {...field}
            name={field.name}
            label={label || ""}
            required={required}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            size={size}
            appearance={appearance}
            autoComplete={autoComplete}
            readOnly={readOnly}
            value={field.value || ""}
            contentAfter={contentAfter}
            setFieldValue={field.onChange}
          />
        </Field>
      )}
    />
  );
};

export default HookFormTextInputField;
